// ###### IMPORTS
import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { createAsset } from '../../viewServices/assets_api';
import { catalogLocationNames } from '../../viewServices/asset_location_catalog';
import { useAssetsData } from '../../../moduleServices/assets_data_context';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

// ###### HELPERS
const getInitialState = () => ({
  asset_code_suffix: '',
  asset_counter: '',
  asset_name: '',
  category_code: '',
  location_name: '',
  asset_type: 'tradeable',
  external_ticker: '',
  is_updated: false,
  is_active: true,
  update_mode: 'not_applied',
});

// ###### COMPONENT
const ModalAssetAdd = ({ isOpen, onClose, onSuccess, assets = [] }) => {
  const { categories, locations, loading: assetsDataLoading } = useAssetsData();
  const [form, setForm] = useState(getInitialState);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(getInitialState());
  }, [isOpen]);

  const loadingCategories = assetsDataLoading && categories.length === 0;

  useEffect(() => {
    if (!form.category_code) {
      setForm((prev) => ({ ...prev, asset_counter: '' }));
      return;
    }
    const categoryAssets = assets.filter((a) => a.category_code === form.category_code);
    const maxCounter = categoryAssets.reduce((max, a) => {
      const c = parseInt(a.asset_counter, 10);
      return !isNaN(c) && c > max ? c : max;
    }, 0);
    setForm((prev) => ({ ...prev, asset_counter: maxCounter + 1 }));
  }, [form.category_code, assets]);

  const fullAssetCode = useMemo(() => {
    const cat = form.category_code;
    const cnt = form.asset_counter !== '' ? String(form.asset_counter).padStart(3, '0') : '???';
    const suffix = form.asset_code_suffix.trim();
    if (!cat) return '';
    return [cat, cnt, suffix].filter(Boolean).join('_');
  }, [form.category_code, form.asset_counter, form.asset_code_suffix]);

  const locationNamesCatalog = useMemo(() => catalogLocationNames(locations), [locations]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'is_updated') {
      setForm((prev) => ({
        ...prev,
        is_updated: checked,
        ...(!checked ? { update_mode: 'not_applied', external_ticker: '' } : {}),
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category_code) {
      toast.error('Select a category.');
      return;
    }
    if (!form.asset_code_suffix?.trim()) {
      toast.error('Asset code suffix is required.');
      return;
    }
    if (!form.asset_name?.trim()) {
      toast.error('Asset name is required.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        asset_code: fullAssetCode,
        asset_counter: parseInt(form.asset_counter, 10) || null,
        asset_name: form.asset_name.trim(),
        asset_type: form.asset_type || 'tradeable',
        category_code: form.category_code,
        location_name: form.location_name?.trim() || null,
        external_ticker:
          form.asset_type === 'tradeable' && form.is_updated
            ? (form.external_ticker?.trim() || null)
            : null,
        is_updated: form.asset_type === 'tradeable' && Boolean(form.is_updated),
        is_active: Boolean(form.is_active),
        update_mode:
          form.asset_type === 'tradeable' && form.is_updated
            ? (form.update_mode || 'not_applied')
            : 'not_applied',
      };
      await createAsset(payload);
      toast.success('Asset created.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        err.response?.data?.asset_code?.[0] ||
        err.message ||
        'Failed to create asset.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative max-h-[90vh] overflow-y-auto">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            title="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4 pr-10">New asset</h2>

        <form className="space-y-4" onSubmit={handleSubmit}>

          {/* Category + counter */}
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Category &amp; code</label>
            <div className="flex items-center gap-2">
              <select
                name="category_code"
                value={form.category_code}
                onChange={handleInputChange}
                disabled={submitting || loadingCategories}
                className="flex-1 min-w-0 px-2 py-1.5 border rounded-md text-sm bg-white"
              >
                <option value="">{loadingCategories ? 'Loading…' : 'Select'}</option>
                {categories.map((cat) => {
                  const l1 = String(cat.level_1 ?? '').trim();
                  const label = l1 ? `${l1} - ${cat.category_code}` : cat.category_code;
                  return (
                    <option key={cat.category_code} value={cat.category_code}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <input
                type="text"
                value={
                  form.asset_counter !== ''
                    ? String(form.asset_counter).padStart(3, '0')
                    : '—'
                }
                readOnly
                className="shrink-0 w-12 px-0.5 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-600 text-center font-mono"
              />
            </div>

            {/* Code suffix + preview */}
            <input
              type="text"
              name="asset_code_suffix"
              value={form.asset_code_suffix}
              onChange={handleInputChange}
              placeholder="Code suffix (e.g. ABEV3)"
              className="mt-1.5 w-full px-2 py-1.5 border rounded-md text-sm font-mono"
              disabled={submitting}
              required
            />
            <div className="mt-1 px-2 py-1 bg-gray-100 rounded-md font-mono text-xs text-gray-700 tracking-wide min-h-[26px]">
              {fullAssetCode || <span className="text-gray-400">Full asset code preview</span>}
            </div>
          </div>

          {/* Name + active */}
          <div className="flex items-end gap-3">
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Name</label>
              <input
                type="text"
                name="asset_name"
                value={form.asset_name}
                onChange={handleInputChange}
                placeholder="Asset name"
                className="w-full px-2 py-1.5 border rounded-md text-sm"
                disabled={submitting}
                required
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer shrink-0 pb-1.5">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleInputChange}
                disabled={submitting}
                className="h-3.5 w-3.5 accent-green-600"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
              {[
                { value: 'deposit', label: 'Deposit' },
                { value: 'tradeable', label: 'Tradeable' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  disabled={submitting}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      asset_type: value,
                      ...(value === 'deposit'
                        ? { is_updated: false, update_mode: 'not_applied', external_ticker: '' }
                        : {}),
                    }))
                  }
                  className={`px-3 py-1 text-xs font-medium transition-all ${
                    value === 'deposit' ? 'rounded-l-full' : 'rounded-r-full'
                  } ${
                    form.asset_type === value
                      ? 'text-white bg-green-500'
                      : 'text-gray-700 hover:bg-gray-200'
                  } ${submitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Location (Location Catalog) */}
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Location</label>
            <select
              name="location_name"
              value={form.location_name}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm bg-white"
              disabled={submitting || loadingCategories}
            >
              <option value="">{loadingCategories ? 'Loading…' : '— None —'}</option>
              {locationNamesCatalog.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[10px] text-gray-400 leading-snug">
              Values come from Location Catalog (Manage). Stored as plain text on the asset.
            </p>
          </div>

          {/* Market update: tradeable only */}
          {form.asset_type === 'tradeable' ? (
            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-1">
                <input
                  type="checkbox"
                  name="is_updated"
                  checked={form.is_updated}
                  onChange={handleInputChange}
                  disabled={submitting}
                  className="h-3.5 w-3.5 accent-green-600"
                />
                <span className="text-sm text-gray-700">Update price from market</span>
              </label>
              <label className="block text-xs text-gray-500 mb-0.5">Update mode</label>
              <select
                name="update_mode"
                value={form.is_updated ? form.update_mode : 'not_applied'}
                onChange={handleInputChange}
                disabled={submitting || !form.is_updated}
                className="w-full px-2 py-1.5 border rounded-md text-sm bg-white mb-2"
              >
              <option value="not_applied">Not Applied</option>
              <option value="manual">Manual</option>
              <option value="api_yahoo_ibovespa">API Yahoo (Ibovespa)</option>
              <option value="api_yahoo_crypto">API Yahoo (Crypto)</option>
              </select>
              <label className="block text-xs text-gray-500 mb-0.5">External ticker (B3)</label>
              <input
                type="text"
                name="external_ticker"
                value={form.external_ticker}
                onChange={handleInputChange}
                placeholder="e.g. ABEV3"
                className="w-full px-2 py-1.5 border rounded-md text-sm font-mono"
                disabled={submitting || !form.is_updated}
              />
            </div>
          ) : null}

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 h-10 text-white rounded-2xl ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 h-10 rounded-2xl border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalAssetAdd;
